import { create } from "zustand";
import { persist, type PersistOptions } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

import { RateLimitStatus } from "../services/ai";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: number;
  type?: "context";
  contextLabel?: string;
}

interface AiState {
  enabled: boolean;
  panelOpen: boolean;
  panelMinimized: boolean;
  ownerUserId: string | null;
  chatMessages: ChatMessage[];
  isProcessing: boolean;
  lastReviewScore: number | null;
  lastReviewSuggestions: string[];
  rateLimit: RateLimitStatus | null;
  pendingSend: string | null;
}

interface AiActions {
  setEnabled: (enabled: boolean) => void;
  setPanelOpen: (open: boolean) => void;
  togglePanel: () => void;
  setPanelMinimized: (minimized: boolean) => void;
  toggleMinimize: () => void;
  addMessage: (message: ChatMessage) => void;
  clearChat: () => void;
  bindUser: (userId: string | null) => void;
  unbindUser: () => void;
  setProcessing: (processing: boolean) => void;
  setReviewResult: (score: number | null, suggestions: string[]) => void;
  setRateLimit: (rateLimit: RateLimitStatus | null) => void;
  setPendingSend: (text: string | null) => void;
}

type AiPersisted = Pick<AiState, "ownerUserId" | "chatMessages">;

export const useAiStore = create<AiState & AiActions>()(
  persist(
    immer((set) => ({
      enabled: true,
      panelOpen: false,
      panelMinimized: false,
      ownerUserId: null,
      chatMessages: [],
      isProcessing: false,
      lastReviewScore: null,
      lastReviewSuggestions: [],
      rateLimit: null,
      pendingSend: null,

      setEnabled: (enabled) => set((state) => { state.enabled = enabled; }),
      setPanelOpen: (open) =>
        set((state) => {
          state.panelOpen = open;
          if (open) state.panelMinimized = false;
        }),
      togglePanel: () =>
        set((state) => {
          if (state.panelOpen && !state.panelMinimized) {
            state.panelOpen = false;
          } else if (state.panelOpen && state.panelMinimized) {
            state.panelMinimized = false;
          } else {
            state.panelOpen = true;
            state.panelMinimized = false;
          }
        }),
      setPanelMinimized: (minimized) => set((state) => { state.panelMinimized = minimized; }),
      toggleMinimize: () => set((state) => { state.panelMinimized = !state.panelMinimized; }),
      addMessage: (message) =>
        set((state) => {
          state.chatMessages.push(message);
          if (state.chatMessages.length > 100) {
            state.chatMessages = state.chatMessages.slice(-100);
          }
        }),
      clearChat: () =>
        set((state) => {
          state.chatMessages = [];
          state.lastReviewScore = null;
          state.lastReviewSuggestions = [];
          state.pendingSend = null;
          state.isProcessing = false;
        }),
      bindUser: (userId) =>
        set((state) => {
          if (!userId || state.ownerUserId === userId) return;
          const switching = state.ownerUserId !== null;
          state.ownerUserId = userId;
          if (!switching) return;
          state.chatMessages = [];
          state.panelOpen = false;
          state.panelMinimized = false;
          state.lastReviewScore = null;
          state.lastReviewSuggestions = [];
          state.pendingSend = null;
          state.isProcessing = false;
        }),
      unbindUser: () =>
        set((state) => {
          state.ownerUserId = null;
          state.chatMessages = [];
          state.panelOpen = false;
          state.panelMinimized = false;
          state.lastReviewScore = null;
          state.lastReviewSuggestions = [];
          state.pendingSend = null;
          state.isProcessing = false;
        }),
      setProcessing: (processing) => set((state) => { state.isProcessing = processing; }),
      setReviewResult: (score, suggestions) =>
        set((state) => {
          state.lastReviewScore = score;
          state.lastReviewSuggestions = suggestions;
        }),
      setRateLimit: (rateLimit) => set((state) => { state.rateLimit = rateLimit; }),
      setPendingSend: (text) => set((state) => { state.pendingSend = text; }),
    })),
    {
      name: "ai-assistant",
      version: 4,
      migrate: () =>
        ({
          ownerUserId: null,
          chatMessages: [],
        }) as unknown as AiState & AiActions,
      partialize: (state) => ({
        ownerUserId: state.ownerUserId,
        chatMessages: state.chatMessages.slice(-20),
      }),
    } as PersistOptions<AiState & AiActions, AiPersisted>,
  ),
);
